/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useRef, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {UiTabSwitcherItem} from 'components/Ui/UiTabSwitcher'
import {toast} from 'utils/toastUtils'
import {closeSecondaryModal} from 'helpers/actions'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {useAppDispatch} from 'helpers/store'
import {OffersAction} from 'store/offers/OffersAction'
import {useTypedSelector} from 'helpers/reducers'
import UiSelectWithGraphics, {type UiSelectOption} from 'components/Ui/UiSelectWithGraphics'
import {PaymentType} from 'types/enums/PaymentType'
import UiTextarea from 'components/Ui/UiTextarea'

interface BookingSellerDuplicateOfferModalProps {
    offer_id: number
    title?: string
    options?: UiTabSwitcherItem[]
    defaultOption?: UiTabSwitcherItem
    closeModal?: () => void
}

const paymentTypeSchema = z.object({
    offer_id: z.number().int().positive('Missing offer ID'),
    payment_type: z.nativeEnum(PaymentType),
    custom_text: z.string().trim().max(1000).nullable(),
}).superRefine((data, context) => {
    if (data.payment_type === PaymentType.Individual && !data.custom_text?.trim()) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['custom_text'],
            message: 'Custom payment instructions are required',
        })
    }
})

type PaymentTypeInputs = z.infer<typeof paymentTypeSchema>

const BookingSellerPaymentTypeModal = (props: BookingSellerDuplicateOfferModalProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const submitting = useRef(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)
    const storedPaymentType = selectedOffer?.extra?.payment_type
    const initialPaymentType = Object.values(PaymentType).includes(storedPaymentType as PaymentType)
        ? storedPaymentType as PaymentType
        : PaymentType.Individual
    const initialCustomText = initialPaymentType === PaymentType.Individual && storedPaymentType !== PaymentType.Individual
        ? storedPaymentType ?? ''
        : null

    const { handleSubmit, setValue, watch, register, formState: {errors} } = useForm<PaymentTypeInputs>({
        resolver: zodResolver(paymentTypeSchema),
        defaultValues: {
            offer_id: props.offer_id,
            payment_type: initialPaymentType,
            custom_text: initialCustomText,
        },
    })

    const selectedPaymentType = watch('payment_type')

    const itemsForOptions: UiSelectOption[] = [
        {
            value: PaymentType.AllInvoice,
            label: t('All items on invoice'),
            description: 'Ihr gewählter Abrechnungsmodus ist <b>Alles auf Gesamtrechnung</b>',
        },
        {
            value: PaymentType.OfferInvoice,
            label: t('Offer items on invoice'),
            description:
                'Ihr gewählter Abrechnungsmodus ist <b>Offertebestandteile auf Gesamtrechnung, restliche Konsumation zahlt jeder Teilnehmer selbst beim Check-out</b>',
        },
        {
            value: PaymentType.AllCheckout,
            label: t('All items on checkout'),
            description: 'Ihr gewählter Abrechnungsmodus ist <b>Alles wird vor Ort beim Check-out bezahlt</b>',
        },
        { value: PaymentType.Individual, label: t('Individually set in Control app') },
    ]

    const onSubmitHandler = (data: PaymentTypeInputs) => {
        if (submitting.current) return
        submitting.current = true
        setIsSubmitting(true)
        setSubmitError(null)

        dispatch(
            OffersAction.fetchChangeOfferPaymentType({
                offer_id: data.offer_id,
                payment_type: data.payment_type,
                custom_text: data.custom_text?.trim() || null,
            })
        )
            .then(() => {
                toast.success(t('Payment type has been successfully updated.'))
                if (props.closeModal) props.closeModal()
                else dispatch(closeSecondaryModal())
            })
            .catch((err) => {
                const status = err?.response?.status ?? err?.status
                const message = status === 403
                    ? t('Access denied')
                    : t('Failed to update payment type. Please try again.')
                setSubmitError(message)
                toast.error(message)
                if (status === 401) navigate('/login', {replace: true})
            })
            .finally(() => {
                submitting.current = false
                setIsSubmitting(false)
            })
    }

    const setSelectedOptionHandler = (value: string | number | undefined) => {
        if (value !== undefined) {
            setValue('payment_type', value as PaymentType, {shouldValidate: true})
        }
    }

    return (
        <form
            className={clsx('BookingSellerPaymentTypeModal flex flex-col grow')}
            onSubmit={handleSubmit(onSubmitHandler)}
        >
            <UiFormInner>
                {submitError && <p role="alert" className="px-6 pt-4 text-red-600 dark:text-red-400">{submitError}</p>}
                <div className={clsx('rounded-xl p-6')}>
                    <input type="hidden" {...register('offer_id', { valueAsNumber: true })} />
                    <div className="flex flex-col gap-4">
                        <UiSelectWithGraphics
                            label={t('Payment Type')}
                            options={itemsForOptions}
                            selected={selectedPaymentType}
                            setSelected={(value) => setSelectedOptionHandler(value)}
                        />
                        {selectedPaymentType == PaymentType.Individual && (
                            <>
                                <span>"Ihr gewählter Abrechnungsmodus ist "</span>
                                <UiTextarea
                                    {...register('custom_text')}
                                    placeholder={t('Enter custom text...')}
                                    rows={4}
                                    error={errors.custom_text?.message}
                                />
                            </>
                        )}
                    </div>
                </div>
            </UiFormInner>

            <UiFormActionsBar
                primaryText={isSubmitting ? t('Executing...') : t('Execute')}
            onPrimary={handleSubmit(onSubmitHandler, () => toast.error(t('Please fix validation errors')))}
                loading={isSubmitting}
            />
        </form>
    )
}

export default BookingSellerPaymentTypeModal
